import React, { useState, useMemo, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash.isequal';
import {
  i18next,
  dialogs,
  site,
  integration,
  anodum,
  template,
  view,
  customHtml,
  dispatchAwaitableAction,
} from '@yola/ws-sdk';
import { reactHookForm } from '@yola/ws-ui';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import upsell from 'src/js/modules/upsell';
import segment from 'src/js/modules/analytics/segment';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import getLanguageCodes from 'src/js/modules/website-settings/helpers/get-language-codes';
import yolaSite from 'yola-editor/src/js/modules/site';
import saveSite from 'src/js/modules/saving/helpers/save-site';
import PageSettings from '../components/page-settings';
import useScrollToError from '../hooks/use-scroll-to-error';
import getKeywordsString from '../helpers/get-keywords-string';
import getKeywordsList from '../helpers/get-keywords-list';
import trimLocaleSuffixFromPath from '../helpers/trim-locale-suffix-from-path';
import appendLocaleSuffixToPath from '../helpers/append-locale-suffix-to-path';
import getLanguageCodesOptions from '../helpers/get-language-codes-options';
import getPathPrefix from '../helpers/get-path-prefix';
import getFieldNamesByTabId from '../helpers/get-field-names-by-tab-id';
import getTabIdByFieldName from '../helpers/get-tab-id-by-field-name';
import deduplicateKeywords from '../helpers/deduplicate-keywords';
import constants from '../constants';
import removeIdsFromCustomCodeSnippets from '../../common/helpers/remove-ids-from-custom-code-snippets';
import addIdsToCustomCodeSnippets from '../../common/helpers/add-ids-to-custom-code-snippets';

const { useForm, useFormState } = reactHookForm;

const {
  trackers: { trackDialogActiveTabChanged },
} = segment;
const { common, tabIdentifiers, fieldNames } = constants;
const { fieldNames: customCodeFieldNames } = customHtml.constants;
const customCodeFieldNamesList = Object.values(customCodeFieldNames);

const getPageSettingsCaptions = () => ({
  apply: i18next.t('Submit'),
  cancel: i18next.t('Cancel'),
  pageSettings: i18next.t('Page Settings'),
  general: {
    tabLabel: i18next.t('General'),
    pageName: {
      name: i18next.t('Page name'),
    },
    displayInNavigation: {
      label: i18next.t('Display in navigation'),
    },
    pageLocale: {
      name: i18next.t('Language Code'),
      description: i18next.t(
        'Set page language code to allow browsers, translation apps, and other tools to perform language-sensitive tasks.'
      ),
      placeholder: i18next.t('Select...'),
    },
    passwordProtectedPages: {
      title: i18next.t('Page access control'),
      subtitle: i18next.t('Manage the access to the content of your page'),
      openedOptionLabel: i18next.t('Open for everyone'),
      protectedOptionLabel: i18next.t('Password protected'),
      fieldTitle: i18next.t('Password'),
      fieldPlaceholder: i18next.t('Enter password for this page'),
      fieldSubtitle: i18next.t(
        'Set a password to ensure that only authorized visitors can view this page. The settings apply after publishing the website.'
      ),
    },
  },
  seoSettings: {
    tabLabel: i18next.t('SEO'),
    searchPreview: {
      heading: i18next.t('Search results preview'),
      title: i18next.t('Your page title'),
      description: i18next.t(
        'Search engines will create a default description for your page, but to override that description, type your own below. This is what viewers see in search results, so use it to show what makes your page special!'
      ),
    },
    title: {
      name: i18next.t('Title tag'),
      description: i18next.t(
        'This is your page title on Google. Add your page name, site name & what you do.'
      ),
    },
    description: {
      name: i18next.t('Meta description'),
      description: i18next.t(
        'Add a brief summary of your page content. If left blank, search engines may use your page content instead.'
      ),
    },
    preventIndexing: {
      name: i18next.t('Hide this page from search results'),
      description: i18next.t(
        'Prevent your page from being indexed by search engines to hide it in Google search'
      ),
    },
    keywords: {
      name: i18next.t('Meta keywords'),
      description: i18next.t(
        'Add page meta keywords. Optimal description length is 155 to 300 characters. Maximum number of keywords is 100'
      ),
    },
    canonicalUrl: {
      name: i18next.t('Canonical URL'),
      description: i18next.t(
        'Point search engines to your preferred page URL to avoid duplicate content.'
      ),
    },
    sameAsPageUrl: {
      label: i18next.t('Same as page URL'),
    },
    sameAsPageName: {
      label: i18next.t('Same as page name'),
    },
    displayBusinessName: {
      label: i18next.t('Include your Business Name in the page title'),
      description: i18next.t(
        'To use the Business Name in the page title, you need to add your business name in {pathToBusinessInfo}',
        {
          pathToBusinessInfo: '',
        }
      ),
      settingsTitle: i18next.t('Settings'),
      businessInfoTitle: i18next.t('Business Info'),
    },
  },
  openGraph: {
    tabLabel: i18next.t('Open Graph'),
    preview: {
      heading: i18next.t('Open Graph preview'),
      title: i18next.t('Your page title'),
      description: i18next.t(
        'Search engines will create a default description for your page, but to override that description, type your own below. This is what viewers see in search results, so use it to show what makes your page special!'
      ),
    },
    title: {
      name: i18next.t('Open Graph title'),
      description: i18next.t(
        'Shown as the headline when your page is shared on social media. Keep it under 60 characters.'
      ),
    },
    sameAsPageTitle: {
      label: i18next.t('Same as page SEO title'),
    },
    description: {
      name: i18next.t('Open Graph description'),
      description: i18next.t(
        'The summary shown below the title on social shares. Aim for 120–160 characters.'
      ),
    },
    sameAsMetaDescription: {
      label: i18next.t('Same as meta description'),
    },
    image: {
      name: i18next.t('Open Graph image'),
      button: `${i18next.t('Choose image')}...`,
      description: i18next.t(
        'The preview image shown when shared on Facebook, LinkedIn, etc. Use 1200×630px, JPG or PNG, under 1MB.'
      ),
      empty: i18next.t('No file chosen'),
    },
  },
  customCode: {
    tabLabel: i18next.t('Custom code'),
    description: i18next.t(
      'Tweak the site by adding custom code, and integration snippets. The code added here will only apply to this page and will appear after the site-wide custom code.'
    ),
    headCode: {
      name: i18next.t('Inside <head> tag'),
    },
    headerCode: {
      name: i18next.t('After <body> tag'),
    },
    footerCode: {
      name: i18next.t('Before </body> tag'),
    },
  },
  confirmExit: {
    title: i18next.t('Save changes?'),
    description: i18next.t('Do you want to save changes you made in page settings?'),
    discard: i18next.t('Discard'),
    submit: i18next.t('Save'),
    cancel: i18next.t('Cancel'),
  },
  discardChanges: {
    title: i18next.t('Discard changes?'),
    description: i18next.t('Do you want to discard changes you made in page settings?'),
    discard: i18next.t('Discard'),
    cancel: i18next.t('Cancel'),
  },
});

const PageSettingsContainer = ({
  onDialogMainAction,
  onDialogCancel,
  pageId,
  activeTabId: tabId = tabIdentifiers.GENERAL,
  formData = null,
  showPromptForce = false,
}) => {
  const dispatch = useDispatch();
  const upsells = useSelector(integration.selectors.getUpsells);
  const siteURL = useSelector(yolaSite.selectors.getSiteURL);
  const pageSettings = useSelector((state) => site.selectors.getPage(state, pageId));
  const siteSettings = useSelector(site.selectors.getSettings);

  const captions = getPageSettingsCaptions({
    ...(upsells.customCode && { customCodeUpgradeText: upsells.customCode.captions }),
  });
  const languageCodes = useMemo(() => getLanguageCodes(), []);
  const languageCodeOptions = useMemo(
    () => getLanguageCodesOptions(languageCodes),
    [languageCodes]
  );
  const isMultilingualSite = site.verifiers.isMultilingualSite();
  const isDisplayInNavigationAvailable =
    template.verifiers.isVariableGlobalsAvailable('navigation');
  const isPasswordProtectedPagesFeatureAvailable = upsell.verifiers.isFeatureAvailable(
    upsell.features.PASSWORD_PROTECTED_PAGES
  );
  const isHomePage = site.verifiers.isHomePage(pageSettings.path, languageCodes);

  const [activeTabId, setActiveTabId] = useState(tabId);
  const [shouldScrollToError, setShouldScrollToError] = useState(false);
  const [passwordProtection, setPasswordProtection] = useState(Boolean(pageSettings.password));

  const [featureFlags] = useFeatureFlags([
    'business_info_in_website_settings',
    'password_protected_pages',
  ]);
  const {
    business_info_in_website_settings: isBusinessInfoInWebsiteSettingsFeatureFlagEnabled,
    password_protected_pages: isPasswordProtectedPagesFeatureFlagEnabled,
  } = featureFlags;

  const initialName = pageSettings.name || pageSettings.title;
  const initialPath = isMultilingualSite
    ? trimLocaleSuffixFromPath(pageSettings.path)
    : pageSettings.path;
  const initialPassword = pageSettings.password || '';
  const initialSameAsPageName =
    pageSettings.sameAsPageName !== undefined ? pageSettings.sameAsPageName : true;
  const initialPageTitle =
    initialSameAsPageName && !!pageSettings.name ? pageSettings.name : pageSettings.title;
  const initialDisplayBusinessName = Boolean(
    pageSettings.displayBusinessName && siteSettings.businessName
  );
  const initialPreventIndexing = pageSettings.preventIndexing || false;
  const initialDescription = pageSettings.description || '';
  const initialKeywords = getKeywordsList(pageSettings.keywords);
  const initialOpenGraphSameAsPageTitle =
    pageSettings[fieldNames.USE_PAGE_TITLE_FOR_OG_TITLE] !== undefined
      ? pageSettings[fieldNames.USE_PAGE_TITLE_FOR_OG_TITLE]
      : true;
  const initialOpenGraphSameAsMetaDescription =
    pageSettings[fieldNames.USE_META_DESCRIPTION_FOR_OG_DESCRIPTION] !== undefined
      ? pageSettings[fieldNames.USE_META_DESCRIPTION_FOR_OG_DESCRIPTION]
      : true;
  const initialOpenGraphTitle = initialOpenGraphSameAsPageTitle
    ? initialPageTitle
    : pageSettings.openGraphTitle || '';
  const initialOpenGraphDescription = initialOpenGraphSameAsMetaDescription
    ? initialDescription
    : pageSettings.openGraphDescription || '';
  const initialOpenGraphImage = pageSettings.openGraphImage || '';
  const initialSameAsPageUrl = pageSettings.usePageUrlForCanonicalUrl || false;
  const canonicalSiteUrl = siteURL || `${common.DEFAULT_SITE_URL}.com`;
  const fullPageUrlByPath = (path) =>
    `${getPathPrefix(canonicalSiteUrl, pageSettings.locale)}${path}`;
  const initialCanonicalUrl = initialSameAsPageUrl
    ? fullPageUrlByPath(initialPath)
    : pageSettings.canonicalUrl || '';

  const {
    control,
    getValues,
    setValue,
    trigger: triggerValidation,
  } = useForm({
    mode: 'onTouched',
    defaultValues: formData || {
      [fieldNames.NAME]: initialName,
      [fieldNames.PATH]: initialPath,
      [fieldNames.HTML_LOCALE]: pageSettings.htmlLocale,
      [fieldNames.DISPLAY_IN_NAVIGATION]: pageSettings.displayInNavigation,
      ...(isPasswordProtectedPagesFeatureFlagEnabled &&
        passwordProtection && {
          [fieldNames.PASSWORD]: initialPassword,
        }),
      [fieldNames.TITLE]: initialPageTitle,
      [fieldNames.SAME_AS_PAGE_NAME]: initialSameAsPageName,
      ...(isBusinessInfoInWebsiteSettingsFeatureFlagEnabled && {
        [fieldNames.DISPLAY_BUSINESS_NAME]: initialDisplayBusinessName,
      }),
      [fieldNames.PREVENT_INDEXING]: initialPreventIndexing,
      [fieldNames.DESCRIPTION]: initialDescription,
      [fieldNames.KEYWORDS]: initialKeywords,
      [fieldNames.OG_TITLE]: initialOpenGraphTitle,
      [fieldNames.USE_PAGE_TITLE_FOR_OG_TITLE]: initialOpenGraphSameAsPageTitle,
      [fieldNames.OG_DESCRIPTION]: initialOpenGraphDescription,
      [fieldNames.USE_META_DESCRIPTION_FOR_OG_DESCRIPTION]: initialOpenGraphSameAsMetaDescription,
      [fieldNames.OG_IMAGE]: initialOpenGraphImage,
      [fieldNames.CANONICAL_URL]: initialCanonicalUrl,
      [fieldNames.USE_PAGE_URL_FOR_CANONICAL_URL]: initialSameAsPageUrl,
      ...Object.fromEntries(
        customCodeFieldNamesList.map((type) => [
          [type],
          addIdsToCustomCodeSnippets(pageSettings[type] || []),
        ])
      ),
    },
  });
  const { dirtyFields, errors } = useFormState({
    control,
  });
  const hasErrors = Object.keys(errors).length > 0;

  const { scrollToError, fieldRefs } = useScrollToError({
    control,
    activeTabId,
  });

  const formValues = getValues();

  const setActiveTabIdWithError = (fieldNamesWithErrors) => {
    const isActiveTabContainsFieldNameWithError = fieldNamesWithErrors.some((fieldName) =>
      getFieldNamesByTabId(activeTabId).includes(fieldName)
    );
    const tabIdWithError = isActiveTabContainsFieldNameWithError
      ? activeTabId
      : getTabIdByFieldName(fieldNamesWithErrors[0]);

    setActiveTabId(tabIdWithError);
    setShouldScrollToError(true);
  };

  const handleSubmit = async () => {
    setValue(fieldNames.KEYWORDS, deduplicateKeywords(getValues(fieldNames.KEYWORDS)));
    await triggerValidation();

    const fieldNamesWithErrors = Object.keys(errors);

    if (fieldNamesWithErrors.length) {
      setActiveTabIdWithError(fieldNamesWithErrors);
      return;
    }

    const settings = getValues();

    const displayInNavigation = isDisplayInNavigationAvailable
      ? settings.displayInNavigation
      : pageSettings.displayInNavigation;
    const password = passwordProtection && settings.password ? settings.password : null;
    const path = isMultilingualSite
      ? appendLocaleSuffixToPath(settings.path, pageSettings.locale)
      : settings.path;
    const keywords = getKeywordsString(settings.keywords);
    const openGraphTitle = settings[fieldNames.OG_TITLE]?.trim() || undefined;
    const openGraphDescription = settings[fieldNames.OG_DESCRIPTION]?.trim() || undefined;
    const openGraphImage = settings[fieldNames.OG_IMAGE] || undefined;
    const usePageTitleForOgTitle = settings[fieldNames.USE_PAGE_TITLE_FOR_OG_TITLE];
    const useMetaDescriptionForOgDescription =
      settings[fieldNames.USE_META_DESCRIPTION_FOR_OG_DESCRIPTION];
    const canonicalUrl = settings[fieldNames.CANONICAL_URL]?.trim() || undefined;
    const usePageUrlForCanonicalUrl =
      settings[fieldNames.USE_PAGE_URL_FOR_CANONICAL_URL] || undefined;
    const isCustomCodeSettingsForUpdate = customCodeFieldNamesList.some(
      (type) => dirtyFields[type]
    );

    const preparedSettings = {
      ...settings,
      displayInNavigation,
      password,
      path,
      keywords,
      [fieldNames.OG_TITLE]: openGraphTitle,
      [fieldNames.USE_PAGE_TITLE_FOR_OG_TITLE]: usePageTitleForOgTitle,
      [fieldNames.OG_DESCRIPTION]: openGraphDescription,
      [fieldNames.USE_META_DESCRIPTION_FOR_OG_DESCRIPTION]: useMetaDescriptionForOgDescription,
      [fieldNames.OG_IMAGE]: openGraphImage,
      [fieldNames.CANONICAL_URL]: canonicalUrl,
      [fieldNames.USE_PAGE_URL_FOR_CANONICAL_URL]: usePageUrlForCanonicalUrl,
      ...Object.fromEntries(
        customCodeFieldNamesList.map((type) => [
          [type],
          removeIdsFromCustomCodeSnippets(settings[type]),
        ])
      ),
    };

    onDialogMainAction(preparedSettings);
    await dispatchAwaitableAction(site.actions.updatePageSettings(pageId, preparedSettings));
    saveSite();

    if (isCustomCodeSettingsForUpdate) {
      dispatch(view.actions.forceReloadView());
    }

    dialogs.operations.hide();
  };

  const handleCancel = () => {
    onDialogCancel();
    dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
  };

  const handleTabChange = (newTabId) => {
    setActiveTabId(newTabId);
    trackDialogActiveTabChanged({ dialogId: dialogTypes.PAGE_MANAGER, tabId: newTabId });
  };

  const handleEnablePasswordProtection = () => {
    if (!isPasswordProtectedPagesFeatureAvailable) {
      upsells.passwordProtectedPages
        .onUpgrade()
        .then(() => {
          setPasswordProtection(true);
        })
        .catch(() => {
          // eslint-disable-next-line no-console
          console.log('Upgrade flow was canceled');
        });

      return;
    }

    setPasswordProtection(true);
  };

  const handleDisablePasswordProtection = () => {
    setPasswordProtection(false);
  };

  const handleChangePageName = () => {
    const [shouldSeoTitleBeSameAsPageName, shouldOpenGraphTitleBeSameAsSeoTitle, pageName] =
      getValues([
        fieldNames.SAME_AS_PAGE_NAME,
        fieldNames.USE_PAGE_TITLE_FOR_OG_TITLE,
        fieldNames.NAME,
      ]);

    if (shouldSeoTitleBeSameAsPageName) {
      setValue(fieldNames.TITLE, pageName);

      if (shouldOpenGraphTitleBeSameAsSeoTitle) {
        setValue(fieldNames.OG_TITLE, pageName);
      }
    }
  };

  const handleChangeSameAsPageName = () => {
    const [shouldSeoTitleBeSameAsPageName, shouldOpenGraphTitleBeSameAsSeoTitle, pageName] =
      getValues([
        fieldNames.SAME_AS_PAGE_NAME,
        fieldNames.USE_PAGE_TITLE_FOR_OG_TITLE,
        fieldNames.NAME,
      ]);

    if (shouldSeoTitleBeSameAsPageName) {
      setValue(fieldNames.TITLE, pageName, { shouldValidate: true });

      if (shouldOpenGraphTitleBeSameAsSeoTitle) {
        setValue(fieldNames.OG_TITLE, pageName, { shouldValidate: true });
      }
    }
  };

  const handleChangeSeoTitle = (title) => {
    const shouldOpenGraphTitleBeSameAsSeoTitle = getValues(fieldNames.USE_PAGE_TITLE_FOR_OG_TITLE);

    if (shouldOpenGraphTitleBeSameAsSeoTitle) {
      setValue(fieldNames.OG_TITLE, title, { shouldValidate: true });
    }
  };

  const handleChangeSeoDescription = (description) => {
    const shouldOpenGraphDescriptionBeSameAsSeoDescription = getValues(
      fieldNames.USE_META_DESCRIPTION_FOR_OG_DESCRIPTION
    );

    if (shouldOpenGraphDescriptionBeSameAsSeoDescription) {
      setValue(fieldNames.OG_DESCRIPTION, description, { shouldValidate: true });
    }
  };

  const handleChangeOpenGraphSameAsPageTitle = () => {
    const [shouldOpenGraphTitleBeSameAsSeoTitle, title] = getValues([
      fieldNames.USE_PAGE_TITLE_FOR_OG_TITLE,
      fieldNames.TITLE,
    ]);

    if (shouldOpenGraphTitleBeSameAsSeoTitle) {
      setValue(fieldNames.OG_TITLE, title, { shouldValidate: true });
    }
  };

  const handleChangeOpenGraphSameAsMetaDescription = () => {
    const [shouldOpenGraphDescriptionBeSameAsMetaDescription, description] = getValues([
      fieldNames.USE_META_DESCRIPTION_FOR_OG_DESCRIPTION,
      fieldNames.DESCRIPTION,
    ]);

    if (shouldOpenGraphDescriptionBeSameAsMetaDescription) {
      setValue(fieldNames.OG_DESCRIPTION, description, { shouldValidate: true });
    }
  };

  const handleOpenGraphImageChange = (imageSource) => {
    setValue(fieldNames.OG_IMAGE, imageSource, { shouldValidate: true });
  };

  const handleChangeCanonicalUrl = () => {
    const [shouldBeSameAsPageUrl, path] = getValues([
      fieldNames.USE_PAGE_URL_FOR_CANONICAL_URL,
      fieldNames.PATH,
    ]);

    if (shouldBeSameAsPageUrl) {
      setValue(fieldNames.CANONICAL_URL, fullPageUrlByPath(path), { shouldValidate: true });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === common.ENTER_KEY) {
      if (anodum.isOneOfTags(e.target, ['input', 'textarea'])) return;
      handleSubmit();
    }
  };

  const showPageSettingsDialog = (data = {}) => {
    const settings = getValues();
    const updatedFormData = { ...settings, ...data.formData };

    dialogs.operations.show(dialogTypes.PAGE_SETTINGS, {
      formData: updatedFormData,
      activeTabId: data.activeTabId,
      showPromptForce: !isEqual(settings, updatedFormData),
      pageId,
    });
  };

  useLayoutEffect(() => {
    if (shouldScrollToError) {
      scrollToError(activeTabId);
      setShouldScrollToError(false);
    }
  }, [activeTabId, scrollToError, shouldScrollToError]);

  return (
    <PageSettings
      activeTabId={activeTabId}
      control={control}
      triggerValidation={triggerValidation}
      fieldRefs={fieldRefs}
      captions={captions}
      pageId={pageId}
      pathPrefix={getPathPrefix(siteURL, pageSettings.locale)}
      businessName={siteSettings.businessName || ''}
      languageCodeOptions={languageCodeOptions}
      isHomePage={isHomePage}
      isDisplayInNavigationAvailable={isDisplayInNavigationAvailable}
      isPasswordProtected={passwordProtection}
      isPasswordProtectedPagesFeatureAvailable={isPasswordProtectedPagesFeatureAvailable}
      isPasswordProtectedPagesFeatureFlagEnabled={Boolean(
        isPasswordProtectedPagesFeatureFlagEnabled
      )}
      isBusinessInfoInWebsiteSettingsFeatureFlagEnabled={
        isBusinessInfoInWebsiteSettingsFeatureFlagEnabled
      }
      showPromptForce={showPromptForce}
      formValues={formValues}
      isValid={!hasErrors}
      onChangePageName={handleChangePageName}
      onChangePagePath={handleChangeCanonicalUrl}
      onChangeSameAsPageName={handleChangeSameAsPageName}
      onChangeSeoTitle={handleChangeSeoTitle}
      onChangeSeoDescription={handleChangeSeoDescription}
      onOpenGraphImageChange={handleOpenGraphImageChange}
      onChangeOpenGraphSameAsPageTitle={handleChangeOpenGraphSameAsPageTitle}
      onChangeOpenGraphSameAsMetaDescription={handleChangeOpenGraphSameAsMetaDescription}
      onChangeSameAsPageUrl={handleChangeCanonicalUrl}
      onEnablePasswordProtection={handleEnablePasswordProtection}
      onDisablePasswordProtection={handleDisablePasswordProtection}
      onTabChange={handleTabChange}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onKeyDown={handleKeyDown}
      onConfirmExitSubmit={handleSubmit}
      onConfirmExitCancel={handleCancel}
      showPageSettingsDialog={showPageSettingsDialog}
    />
  );
};

PageSettingsContainer.propTypes = {
  pageId: PropTypes.string.isRequired,
  activeTabId: PropTypes.string,
  formData: PropTypes.shape({}),
  showPromptForce: PropTypes.bool,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default PageSettingsContainer;
